import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useT } from "../lib/i18n";
import { openBooking } from "../lib/booking";

export default function Services() {
    const { t, lang } = useT();
    const navigate = useNavigate();

    useEffect(() => {
        document.title = lang === "it" ? "Servizi, Casati Consulting" : "Services, Casati Consulting";
    }, [lang]);

    return (
        <div className="bg-[#F9F9F7] text-[#1C1C1C]">
            <section className="pt-20 md:pt-28 pb-12">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-y-8 md:gap-x-8">
                    <div className="col-span-12 md:col-span-7">
                        <p className="eyebrow">{t.servicesPage.eyebrow}</p>
                        <h1
                            className="font-display mt-6 text-5xl sm:text-7xl lg:text-[100px] tracking-tight font-medium leading-[0.92]"
                            style={{ letterSpacing: "-0.035em" }}
                            data-testid="services-title"
                        >
                            {t.servicesPage.titleA}
                            <br />
                            <span className="italic font-light">{t.servicesPage.titleB}</span>
                        </h1>
                    </div>
                    <p className="col-span-12 md:col-span-4 md:col-start-9 self-end text-[#1C1C1C]/70 leading-relaxed text-base md:text-lg">
                        {t.servicesPage.intro}
                    </p>
                </div>
            </section>

            <section className="border-t hair">
                {t.services.list.map((s, idx) => (
                    <div
                        key={s.n}
                        className={`border-b hair py-20 md:py-24 ${idx % 2 === 1 ? "bg-[#F4F3EF]" : ""}`}
                        data-testid={`service-${s.n}`}
                    >
                        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-y-8 md:gap-x-8">
                            <div className="col-span-12 md:col-span-3">
                                <p className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "#9B7A58" }}>
                                    {s.n}, {s.sub}
                                </p>
                                <h2 className="font-display mt-6 text-3xl md:text-4xl tracking-tight font-medium" style={{ letterSpacing: "-0.03em" }}>
                                    {s.t}
                                </h2>
                            </div>
                            <div className="col-span-12 md:col-span-5 md:col-start-5">
                                <p className="text-[#1C1C1C]/80 leading-relaxed text-base md:text-lg">{s.d}</p>
                            </div>
                            <div className="col-span-12 md:col-span-3 md:col-start-10">
                                <p className="eyebrow">{t.services.includes}</p>
                                <ul className="mt-5 space-y-3 text-sm">
                                    {s.b.map((b) => (
                                        <li key={b} className="flex items-start gap-3">
                                            <span className="mt-2 inline-block w-1.5 h-1.5 shrink-0" style={{ background: "#9B7A58" }} />
                                            <span className="text-[#1C1C1C]/80 leading-relaxed">{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                ))}
            </section>

            <section className="border-b hair py-20 md:py-24" data-testid="adjacent-services">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-y-8 md:gap-x-8">
                    <div className="col-span-12 md:col-span-3">
                        <p className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "#9B7A58" }}>
                            {t.services.adjacentEyebrow}
                        </p>
                        <h2 className="font-display mt-6 text-3xl md:text-4xl tracking-tight font-medium" style={{ letterSpacing: "-0.03em" }}>
                            {t.services.adjacentTitle}
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-5 md:col-start-5">
                        <p className="text-[#1C1C1C]/80 leading-relaxed text-base md:text-lg">{t.services.adjacentBody}</p>
                    </div>
                    <div className="col-span-12 md:col-span-3 md:col-start-10">
                        <p className="eyebrow">{t.services.adjacentListEyebrow}</p>
                        <ul className="mt-5 space-y-3 text-sm">
                            {t.services.adjacent.map((it) => (
                                <li key={it} className="flex items-start gap-3" data-testid={`adjacent-item`}>
                                    <span className="mt-2 inline-block w-1.5 h-1.5 shrink-0" style={{ background: "#9B7A58" }} />
                                    <span className="text-[#1C1C1C]/80 leading-relaxed">{it}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <section className="py-24 md:py-32 bg-[#1C1C1C] text-[#F9F9F7]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-8 items-end">
                    <div className="col-span-12 md:col-span-8">
                        <p className="eyebrow">{t.services.engageEyebrow}</p>
                        <h2
                            className="font-display mt-5 text-4xl sm:text-6xl tracking-tight font-medium leading-[0.95]"
                            style={{ letterSpacing: "-0.03em" }}
                        >
                            {t.services.engage}
                            <br />
                            <span className="text-[#9B7A58]">{t.services.engageThen}</span>
                        </h2>
                        <p
                            className="mt-6 text-sm italic text-[#F9F9F7]/65"
                            data-testid="services-free-consultation"
                        >
                            {t.services.freeConsultation}
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-4 flex md:justify-end">
                        <button
                            type="button"
                            onClick={() => openBooking(navigate)}
                            className="inline-flex items-center gap-2 px-6 h-12 bg-[#F9F9F7] text-[#1C1C1C] text-[12px] uppercase tracking-[0.22em] font-bold hover:bg-[#9B7A58] hover:text-[#F9F9F7] transition-colors"
                            data-testid="services-cta"
                        >
                            {t.cta.book}
                            <ArrowUpRight size={16} strokeWidth={1.5} />
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
}
